import React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import FileUpload from '../components/FileUpload';

const FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const SUPPORTED_FORMATS = ["image/jpg", "image/jpeg", "image/png", "application/pdf"];

const documentsValidationSchema = Yup.object().shape({
    proofOfIdentity: Yup.mixed().required('Proof of Identity is required')
        .test('fileSize', 'File too large', value => !value || value.size <= FILE_SIZE)
        .test('fileFormat', 'Unsupported Format', value => !value || SUPPORTED_FORMATS.includes(value.type)),
    proofOfAddress: Yup.mixed().required('Proof of Address is required')
        .test('fileSize', 'File too large', value => !value || value.size <= FILE_SIZE)
        .test('fileFormat', 'Unsupported Format', value => !value || SUPPORTED_FORMATS.includes(value.type)),
    financialAccreditation: Yup.mixed()
        .test('fileSize', 'File too large', value => !value || value.size <= FILE_SIZE)
        .test('fileFormat', 'Unsupported Format', value => !value || SUPPORTED_FORMATS.includes(value.type)),
    signedApplication: Yup.mixed().required('Signed Application is required')
        .test('fileSize', 'File too large', value => !value || value.size <= FILE_SIZE)
        .test('fileFormat', 'Unsupported Format', value => !value || SUPPORTED_FORMATS.includes(value.type)),
    signedAgreement: Yup.mixed().required('Signed Agreement is required')
        .test('fileSize', 'File too large', value => !value || value.size <= FILE_SIZE)
        .test('fileFormat', 'Unsupported Format', value => !value || SUPPORTED_FORMATS.includes(value.type)),
    paymentConfirmation: Yup.mixed().required('Payment Confirmation is required')
        .test('fileSize', 'File too large', value => !value || value.size <= FILE_SIZE)
        .test('fileFormat', 'Unsupported Format', value => !value || SUPPORTED_FORMATS.includes(value.type)),
    passportPhoto: Yup.mixed().required('Passport Photo is required')
        .test('fileSize', 'File too large', value => !value || value.size <= FILE_SIZE)
        .test('fileFormat', 'Unsupported Format', value => !value || SUPPORTED_FORMATS.includes(value.type)),
    termsAccepted: Yup.boolean().oneOf([true], 'You must accept the terms').required('You must accept the terms'),
});

const Step2Form = ({ handleSubmit, setStep }) => (
    <Formik
        initialValues={{
            proofOfIdentity: null,
            proofOfAddress: null,
            financialAccreditation: null,
            signedApplication: null,
            signedAgreement: null,
            paymentConfirmation: null,
            passportPhoto: null,
            termsAccepted: false,
        }}
        validationSchema={documentsValidationSchema}
        onSubmit={handleSubmit}
    >
        {({ isSubmitting }) => (
            <Form className="px-6 py-4 space-y-6">
                <div>
                    <h2 className="text-xl font-semibold text-purple-600 mb-4">Required Documents</h2>
                    <div className="grid grid-cols-1 gap-6">
                        <Field name="proofOfIdentity" component={FileUpload} label="1. Proof of Identity (e.g., Passport, National ID, Driver's License)" />
                        <ErrorMessage name="proofOfIdentity" component="div" className="mt-1 text-sm text-red-600" />

                        <Field name="proofOfAddress" component={FileUpload} label="2. Proof of Address (e.g., Utility Bill, Bank Statement, Lease Agreement)" />
                        <ErrorMessage name="proofOfAddress" component="div" className="mt-1 text-sm text-red-600" />

                        <Field name="financialAccreditation" component={FileUpload} label="3. Proof of Financial Accreditation (if applicable)" />
                        <ErrorMessage name="financialAccreditation" component="div" className="mt-1 text-sm text-red-600" />

                        <Field name="signedApplication" component={FileUpload} label="4. Signed Investor Subscription Application Form" />
                        <ErrorMessage name="signedApplication" component="div" className="mt-1 text-sm text-red-600" />

                        <Field name="signedAgreement" component={FileUpload} label="5. Signed Investor Agreement Terms" />
                        <ErrorMessage name="signedAgreement" component="div" className="mt-1 text-sm text-red-600" />

                        <Field name="paymentConfirmation" component={FileUpload} label="6. Payment Confirmation (for the initial subscription amount)" />
                        <ErrorMessage name="paymentConfirmation" component="div" className="mt-1 text-sm text-red-600" />

                        <Field name="passportPhoto" component={FileUpload} label="7. Passport Photo" />
                        <ErrorMessage name="passportPhoto" component="div" className="mt-1 text-sm text-red-600" />
                    </div>
                </div>

                {/* Terms and Conditions */}
                <div>
                    <h2 className="text-xl font-semibold text-purple-600 mb-4">Acknowledgments and Acceptance</h2>
                    <div className="flex items-start">
                        <div className="flex items-center h-5">
                            <Field type="checkbox" name="termsAccepted" className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="termsAccepted" className="font-medium text-gray-700">
                                I hereby confirm that I have read, understood, and agree to the terms outlined in the Subscription Agreement, Investor Agreement Terms, and all other documents provided. I confirm my eligibility as an investor, acknowledge the risk factors associated, and accept the limitations on transferability.
                            </label>
                            <ErrorMessage name="termsAccepted" component="div" className="mt-1 text-sm text-red-600" />
                        </div>
                    </div>
                </div>

                {/* Back and Submit Buttons */}
                <div className="flex space-x-4">
                    <button
                        type="button"
                        onClick={() => setStep(1)}
                        className="w-1/2 py-2 px-4 bg-white text-gray-700 border border-gray-300 rounded-md shadow-sm"
                    >
                        Back
                    </button>
                    <button
                        type="submit"
                        className="w-1/2 py-2 px-4 bg-purple-600 text-white rounded-md shadow-sm hover:bg-purple-700"
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit Application'}
                    </button>
                </div>
            </Form>
        )}
    </Formik>
);

export default Step2Form;
